import { assessmentAdminError } from '../Assessments/api';

/**
 * One place this module turns a failed request into a sentence an admin can
 * act on.
 *
 * The screens used to render the API envelope verbatim, and it showed. A
 * deleted draft read "Scenario draft could not be loaded / detail: Not found";
 * an unhandled exception read "detail: boom"; a bad job id rendered the
 * literal word "null" as its explanation; and an expired session showed
 * "Given token not valid for any token type" and left the admin sitting on a
 * dead page. None of those says what happened, whether anything was written,
 * or what to do next — which is the whole job of an error.
 *
 * Two rules the helper keeps.
 *
 * It never claims more than the status proves. A 4xx is the request being
 * refused before work started, so it can promise nothing was changed; a 5xx
 * on a read can promise the same, but on a write it can only say what the
 * server told it. The sentences below are written for the read/refuse cases
 * this module actually makes — every write in the module goes through the job
 * ledger, whose own refusal copy is better than anything generic.
 *
 * And it never swallows the server's own words. Where the backend wrote a
 * sentence (every refusal in this product does), that sentence wins and the
 * generic one is not shown at all.
 */

/** A failed request, with the status the sentence is chosen from. */
export class ScenarioApiError extends Error {
  status: number;

  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ScenarioApiError';
    this.status = status;
    this.body = body;
  }
}

/** Where an admin whose session has ended is sent. */
export const SIGN_IN_PATH = '/auth/login';

export const EXPIRED_SESSION_COPY =
  'Your session has expired. Taking you back to sign in.';

/** The first real sentence in a value DRF may have put a message inside. */
const sentenceIn = (value: unknown): string => {
  // "null" and "" are what the raw envelope rendered as an explanation.
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = sentenceIn(item);
      if (found) return found;
    }
    return '';
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      sentenceIn(record.message) ||
      sentenceIn(record.detail) ||
      sentenceIn(record.refusal)
    );
  }
  return '';
};

/**
 * DRF's flat `{detail}`, the module's own `{message}` refusals — and the
 * FIELD-ERROR DICT a refused submission comes back as.
 *
 * A refusal at the door never reaches the job ledger, so the ledger's own
 * copy is not there to save it: the backend's sentence arrives as
 * `{version_id: ['...']}` or `{assessment_attach: {code, message}}`, one
 * level below where this used to look. Reading only the top level turned
 * every one of them into "The request was refused. Nothing was changed." —
 * true, and useless.
 */
const serverSentence = (body: unknown): string => {
  if (!body || typeof body !== 'object') return '';
  const record = body as Record<string, unknown>;
  const top =
    sentenceIn(record.refusal) ||
    sentenceIn(record.message) ||
    sentenceIn(record.detail);
  if (top) return top;
  // Field errors, in the order the server sent them: the first field that
  // carries a sentence is the one an admin has to act on.
  for (const [key, value] of Object.entries(record)) {
    if (key === 'refusal' || key === 'message' || key === 'detail') continue;
    const found = sentenceIn(value);
    if (found) return found;
  }
  return '';
};

/**
 * A 401 that is not a token the refresh flow can renew: the admin has to sign
 * in again, and the screen says so instead of showing them the token error.
 */
export const isExpiredSession = (error: unknown): boolean =>
  error instanceof ScenarioApiError && error.status === 401;

/** Whether the thing being asked for is simply not there any more. */
export const isMissing = (error: unknown): boolean =>
  error instanceof ScenarioApiError && error.status === 404;

type Subject = {
  /** What was being read, in the admin's words: "That draft", "That job". */
  what?: string;
};

/**
 * The sentence to show. `what` names the subject so a 404 can say which thing
 * is gone; without it the copy stays true but general.
 */
export const describeApiError = (
  error: unknown,
  { what }: Subject = {},
): string => {
  if (!(error instanceof ScenarioApiError)) {
    const message = (error as Error)?.message;
    return message || 'The request failed. Nothing was changed.';
  }
  const subject = what || 'What you asked for';
  const sentence = serverSentence(error.body);
  if (error.status === 401) return EXPIRED_SESSION_COPY;
  if (error.status === 403) {
    return 'You do not have permission to see this. Nothing was changed.';
  }
  if (error.status === 404) {
    return `${subject} no longer exists — it may have been archived or deleted.`;
  }
  if (error.status === 409) {
    // A conflict always carries the reason it conflicted, and that reason is
    // the whole message; a generic sentence beside it would be noise.
    return (
      sentence || 'Something else changed this while you were looking at it.'
    );
  }
  if (error.status >= 500) {
    return sentence
      ? `The server had an error. Nothing was changed. ${sentence}`
      : 'The server had an error. Nothing was changed. Try again, or check the Jobs tab for what ran.';
  }
  if (error.status >= 400) {
    return sentence || 'The request was refused. Nothing was changed.';
  }
  return sentence || error.message;
};

/**
 * Sends the admin back to sign in when their session has ended.
 *
 * Deliberately a full navigation rather than a router push: the token is gone,
 * so every screen behind this one is about to fail the same way, and the login
 * page lives outside this SPA's dashboard tree.
 */
export const redirectToSignIn = () => {
  if (typeof window === 'undefined') return;
  window.location.assign(SIGN_IN_PATH);
};

/**
 * The one call every screen makes in its catch block: turn the failure into a
 * sentence, and take an admin whose session has ended somewhere they can do
 * something about it.
 */
export const readableError = (
  error: unknown,
  subject: Subject = {},
): string => {
  if (isExpiredSession(error)) redirectToSignIn();
  return describeApiError(error, subject);
};

/** Builds the error a failed response becomes. Used by both api clients so
 *  status is never lost on the way to the screen. */
export const apiErrorFrom = (status: number, body: unknown) =>
  new ScenarioApiError(assessmentAdminError(body), status, body);
